import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import api from "../utils/api";

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

export default function ItemMasterAdd() {
  const navigate = useNavigate();

  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemType, setItemType] = useState("");
  const [unitName, setUnitName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    async function loadUnits() {
      try {
        const res = await api.get("/api/unit-master");
        setUnits(toArray(res.data));
      } catch (err) {
        console.error("Unit load error:", err);
        setUnits([]);
      }
    }

    loadUnits();
  }, []);

  const unitOptions = useMemo(() => {
    return units
      .map((u) => {
        const code = String(u?.sCode || "").trim();
        const name = String(u?.sName || "").trim();

        if (!code) return null;

        return {
          value: code,
          label: name ? `${code} - ${name}` : code,
          searchText: `${code} ${name}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [units]);

  const selectedUnit =
    unitOptions.find(
      (opt) => String(opt.value).toLowerCase() === String(unitName).toLowerCase()
    ) || null;

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.375rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "6px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "6px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  async function save() {
    if (!itemCode.trim()) return alert("Item Code required");
    if (!itemName.trim()) return alert("Item Name required");
    if (!itemType.trim()) return alert("Item Type required");
    if (!unitName.trim()) return alert("Unit Name required");

    try {
      setSaving(true);

      await api.post("/api/item-master", {
        item_code: itemCode.trim(),
        item_name: itemName.trim(),
        item_type: itemType.trim(),
        unit_name: unitName.trim(),
        is_active: isActive,
      });

      alert("Saved successfully");
      navigate("/item-master");
    } catch (err) {
      alert(err?.response?.data?.error || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Add Item Master</h2>

        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/item-master")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body row g-3">
          <div className="col-md-4">
            <label className="form-label">Item Code</label>
            <input
              className="form-control"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Item Name</label>
            <input
              className="form-control"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Item Type</label>
            <select
              className="form-select"
              value={itemType}
              onChange={(e) => setItemType(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="extruder">extruder</option>
              <option value="injection">injection</option>
              <option value="recycle">recycle</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Unit Name</label>
            <Select
              options={unitOptions}
              value={selectedUnit}
              onChange={(selected) =>
                setUnitName(selected ? selected.value : "")
              }
              placeholder="-- Select Unit --"
              isClearable
              isSearchable
              styles={customSelectStyles}
              noOptionsMessage={() => "No unit found"}
              filterOption={(option, inputValue) => {
                const text =
                  `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                return text.includes(String(inputValue).toLowerCase());
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Active</label>
            <select
              className="form-select"
              value={isActive ? "1" : "0"}
              onChange={(e) => setIsActive(e.target.value === "1")}
            >
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}